import type { AnalyzeResponse } from '../types'

const PROD_API_BASE = 'https://api.spendifique.com'
const STAGING_API_BASE = 'https://api-staging.spendifique.com'
const LOCAL_API_BASE = 'http://localhost:3333'

/**
 * Picks the API host based on where the widget is running. Lets the same
 * embed snippet work on production Webflow, the spendifique-dev.webflow.io
 * staging site, and local development. An explicit `apiBase` passed to
 * mount() always wins so anyone testing against an alternate environment
 * can override.
 */
function resolveApiBase(passed?: string): string {
  if (passed) return passed
  if (typeof window === 'undefined') return PROD_API_BASE
  const host = window.location.hostname
  if (host === 'spendifique-dev.webflow.io') return STAGING_API_BASE
  if (host === 'localhost' || host === '127.0.0.1') return LOCAL_API_BASE
  return PROD_API_BASE
}

/**
 * Thrown when the backend rate-limits us (HTTP 429). The widget surfaces a
 * dedicated view for this so users see a friendly upsell instead of a
 * generic error toast.
 */
export class RateLimitError extends Error {
  constructor(message = 'Rate limit reached') {
    super(message)
    this.name = 'RateLimitError'
  }
}

export async function analyzeInvoice(
  file: File,
  apiBase?: string,
): Promise<AnalyzeResponse> {
  const base = resolveApiBase(apiBase)
  const form = new FormData()
  form.append('file', file)

  const res = await fetch(`${base}/api/tools/invoice-splitter/analyze`, {
    method: 'POST',
    body: form,
  })
  if (res.status === 429) {
    throw new RateLimitError()
  }
  if (!res.ok) {
    throw new Error(`Analyze failed: ${res.status}`)
  }
  return (await res.json()) as AnalyzeResponse
}

export type SendPayload = {
  email: string
  consented: boolean
  termsAccepted: boolean
  source: string
  originalFilename?: string
  selections: { filename: string; pageRange: string }[]
}

export type SendResponse = {
  ok: true
  leadId: number
  sentTo: string
  invoiceCount: number
}

export async function sendInvoices(
  file: File,
  payload: SendPayload,
  apiBase?: string,
): Promise<SendResponse> {
  const base = resolveApiBase(apiBase)
  const form = new FormData()
  form.append('file', file)
  form.append('payload', JSON.stringify(payload))

  const res = await fetch(`${base}/api/tools/invoice-splitter/send`, {
    method: 'POST',
    body: form,
  })
  if (!res.ok) {
    throw new Error(await readError(res, 'Could not send your invoices'))
  }
  return (await res.json()) as SendResponse
}

export type CaptureLeadPayload = {
  email: string
  consented: boolean
  termsAccepted: boolean
  source: string
  metadata?: Record<string, unknown>
}

/**
 * Lightweight lead capture used on the Download path when the user opts in
 * for marketing without using the email-attachment flow. Not awaited
 * blocking-ly by the caller — failures here should not interrupt the user's
 * download.
 */
export async function captureLead(
  payload: CaptureLeadPayload,
  apiBase?: string,
): Promise<void> {
  const base = resolveApiBase(apiBase)
  const res = await fetch(`${base}/api/tools/invoice-splitter/capture-lead`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!res.ok) {
    throw new Error(await readError(res, 'Could not save your email'))
  }
}

async function readError(res: Response, fallback: string): Promise<string> {
  let detail = ''
  try {
    const body = (await res.json()) as { error?: string; message?: string }
    detail = body.error ?? body.message ?? ''
  } catch {
    // ignore
  }
  return detail || `${fallback} (${res.status}). Please try again.`
}
